/**
 * Caprion Rejection Guard Hook
 * 
 * Checks if the user's loan application has been rejected.
 * Use this hook on every Caprion journey page to block navigation if rejected.
 * 
 * Usage:
 * ```tsx
 * const { isRejected, rejectionReason, isChecking } = useCaprionRejectionGuard();
 * 
 * if (isRejected) {
 *   return <RejectionScreen reason={rejectionReason} />;
 * }
 * ```
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

interface RejectionStatus {
  isRejected: boolean;
  rejectionReason: string | null;
  rejectionTimestamp: string | null;
  isChecking: boolean;
}

export function useCaprionRejectionGuard(): RejectionStatus {
  const [isRejected, setIsRejected] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);
  const [rejectionTimestamp, setRejectionTimestamp] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkRejectionStatus = async () => {
      try {
        setIsChecking(true);
        
        const response = await axios.get(
          `${API_BASE_URL}/api/caprion/check-rejection-status/`,
          { withCredentials: true }
        );

        if (response.data.is_rejected) {
          console.log('[REJECTION GUARD] ❌ Application is rejected:', response.data);
          setIsRejected(true);
          setRejectionReason(response.data.rejection_reason || 'Application rejected by lender');
          setRejectionTimestamp(response.data.rejection_timestamp);
        } else {
          console.log('[REJECTION GUARD] ✅ Application is not rejected');
          setIsRejected(false);
        }
      } catch (error: any) {
        console.error('[REJECTION GUARD] Error checking rejection status:', error);
        // Fail open - if we can't check, allow user to proceed
        setIsRejected(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkRejectionStatus();
    
    // No interval - only check once on mount
    // (Polling will handle rejection detection during journey)
  }, []);

  return {
    isRejected,
    rejectionReason,
    rejectionTimestamp,
    isChecking
  };
}
